import React, { useMemo } from 'react';
import { ARABIC_LETTERS, getLetterGrouped } from '../data/dreamsData';

interface LetterViewProps {
  letter: string;
  onSelectLetter: (letter: string) => void;
  onSelectDream: (slug: string) => void;
  onNavigateToDictionary: () => void;
  onStartAiInterpretation: (symbolName?: string) => void;
  onBackToHome: () => void;
}

const preventDefault = (e: React.MouseEvent, handler: () => void) => {
  if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
    e.preventDefault();
    handler();
  }
};

const LetterView: React.FC<LetterViewProps> = ({
  letter,
  onSelectLetter,
  onSelectDream,
  onNavigateToDictionary,
  onStartAiInterpretation,
  onBackToHome
}) => {
  const { enhanced: dreams, plain: filteredDictionaryEntries } = useMemo(() => getLetterGrouped(letter), [letter]);

  const totalCount = dreams.length + filteredDictionaryEntries.length;

  return (
    <div className="min-h-screen w-full bg-[#1E293B] text-slate-100 py-8 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-5xl mx-auto">
        <nav aria-label="مسار التصفح" className="flex items-center gap-2 text-sm text-slate-300 mb-8 pb-4 border-b border-slate-700/60">
          <a href="/" onClick={(e) => preventDefault(e, onBackToHome)} className="hover:text-emerald-400 no-underline">الرئيسية</a>
          <span>/</span>
          <a href="/dictionary" onClick={(e) => preventDefault(e, onNavigateToDictionary)} className="hover:text-emerald-400 no-underline">قاموس الأحلام</a>
          <span>/</span>
          <span className="text-emerald-400 font-semibold">حرف ({letter}) — {totalCount} رمزاً</span>
        </nav>

        <header className="text-center mb-10">
          <div className="w-16 h-16 bg-emerald-500/20 border border-emerald-500/40 rounded-2xl flex items-center justify-center text-3xl font-extrabold text-emerald-400 mx-auto mb-4">
            {letter}
          </div>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-white mb-3">
            تفسير الأحلام والرؤى بحرف ({letter})
          </h1>
          <p className="text-slate-300 text-base max-w-xl mx-auto">
            فهرس شامل لرموز الأحلام التي تبدأ بحرف ({letter}) مستند إلى مصادر التراث التفسيري.
          </p>
        </header>

        <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center mb-10 max-w-4xl mx-auto">
          {ARABIC_LETTERS.map((l) => (
            <a
              key={l}
              href={`/letter/${encodeURIComponent(l)}`}
              onClick={(e) => preventDefault(e, () => onSelectLetter(l))}
              className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl font-bold transition-all text-sm sm:text-base no-underline ${
                l === letter
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/40 scale-105'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
              }`}
            >
              {l}
            </a>
          ))}
        </div>

        {totalCount > 0 ? (
          <>
            {dreams.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-2xl">⭐</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    الرموز المحسنة والمفصلة
                    <span className="text-sm font-normal text-slate-400 mr-2">({dreams.length} رمزاً)</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {dreams.map((dream) => (
                    <a
                      key={dream.slug}
                      href={`/dream/${dream.slug}`}
                      onClick={(e) => preventDefault(e, () => onSelectDream(dream.slug))}
                      className="block no-underline bg-slate-800/60 border border-slate-700/60 hover:border-emerald-500 rounded-2xl p-6 transition-all hover:shadow-xl group cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex flex-wrap gap-2 items-center mb-3">
                          <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2.5 py-1 rounded-md font-semibold border border-emerald-500/20">
                            {dream.category}
                          </span>
                          <a
                            href={`/letter/${encodeURIComponent(dream.letter)}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              preventDefault(e, () => onSelectLetter(dream.letter));
                            }}
                            className="no-underline text-xs bg-teal-500/10 text-teal-400 px-2.5 py-1 rounded-md font-semibold border border-teal-500/20 hover:bg-teal-500/20 transition-colors"
                          >
                            حرف {dream.letter}
                          </a>
                        </div>
                        <h2 className="text-xl font-bold text-white mt-1 mb-2 group-hover:text-emerald-400 transition-colors">
                          {dream.title}
                        </h2>
                        <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-3">
                          {dream.summary}
                        </p>
                      </div>
                      <div className="pt-4 border-t border-slate-700/50 flex items-center justify-between">
                        <span className="text-emerald-400 text-sm font-semibold group-hover:translate-x-[-4px] transition-transform">
                          قراءة التفسير بالكامل ←
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onStartAiInterpretation(dream.name);
                          }}
                          className="text-xs text-slate-200 hover:text-emerald-300 bg-slate-900/80 px-2.5 py-1 rounded-lg border border-slate-700 hover:border-emerald-500 transition-colors"
                        >
                          تفسير مخصص ✨
                        </button>
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {filteredDictionaryEntries.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-2xl">📖</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    بقية رموز قاموس الأحلام بحرف ({letter})
                    <span className="text-sm font-normal text-slate-400 mr-2"> ({filteredDictionaryEntries.length} رمزاً)</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredDictionaryEntries.map((entry) => (
                    <a
                      key={entry.slug}
                      href={`/dream/${entry.slug}`}
                      onClick={(e) => preventDefault(e, () => onSelectDream(entry.slug))}
                      className="block no-underline bg-slate-800/40 border border-slate-700/50 hover:border-teal-500 rounded-xl p-4 transition-all cursor-pointer group flex flex-col justify-between gap-3"
                    >
                      <div>
                        <h3 className="font-bold text-white group-hover:text-teal-300 transition-colors mb-1">{entry.name}</h3>
                        <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{entry.text.slice(0, 150)}</p>
                      </div>
                      <div className="flex flex-wrap items-center justify-between border-t border-slate-700/50 pt-2 gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <a
                            href={`/letter/${encodeURIComponent(entry.letter)}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              preventDefault(e, () => onSelectLetter(entry.letter));
                            }}
                            className="no-underline text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded border border-teal-500/20 hover:bg-teal-500/20 transition-colors"
                          >
                            حرف {entry.letter}
                          </a>
                          <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">{entry.category}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onStartAiInterpretation(entry.name);
                          }}
                          className="text-[10px] text-slate-200 hover:text-emerald-300 bg-slate-900/80 px-2 py-0.5 rounded border border-slate-700"
                        >
                          ✨ ذكاء
                        </button>
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </>
        ) : (
          <div className="bg-slate-800/40 border border-slate-700 rounded-3xl p-12 text-center max-w-lg mx-auto mb-12">
            <span className="text-5xl mb-4 block">🌙</span>
            <h3 className="text-xl font-bold text-white mb-2">جاري إضافة المزيد من الرموز بحرف ({letter})</h3>
            <p className="text-slate-300 text-sm mb-6">
              يمكنك كتابة حلمك مباشرة وسيقوم الذكاء الاصطناعي بتفسير الرموز المرتبطة بهذا الحرف فوراً.
            </p>
            <button
              onClick={() => onStartAiInterpretation()}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg"
            >
              فسر حلمك بالذكاء الاصطناعي الآن ←
            </button>
          </div>
        )}

        {filteredDictionaryEntries.length > 0 && (
          <div className="mb-8 p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-indigo-900/30 via-slate-800/60 to-rose-900/20 border border-indigo-500/30">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-white mb-1">رؤيا مركبة بتفاصيل كثيرة؟</h3>
                <p className="text-sm text-slate-300">
                  اجعل الذكاء الاصطناعي يربط بين جميع الرموز ويُخرج لك تفسيراً متكاملاً يأخذ في الحسبان السياق والتفاصيل.
                </p>
                <p className="text-[11px] text-amber-300 mt-2">
                  ⚠️ تفسير الذكاء الاصطناعي هو اجتهاد آلي ولا يُنسب مباشرة لأحد المفسرين التراثيين.
                </p>
              </div>
              <button
                onClick={() => onStartAiInterpretation()}
                className="shrink-0 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg shadow-emerald-900/30 transition-all"
              >
                ابدأ التفسير المخصص ✨
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-6 border-t border-slate-700/60">
          <a
            href="/dictionary"
            onClick={(e) => preventDefault(e, onNavigateToDictionary)}
            className="text-slate-300 hover:text-white text-sm no-underline"
          >
            ← العودة لقاموس الأحلام
          </a>
          <button
            onClick={() => onStartAiInterpretation()}
            className="text-emerald-400 hover:text-emerald-300 text-sm font-bold"
          >
            تفسير حلم جديد ✨
          </button>
        </div>
      </div>
    </div>
  );
};

export default LetterView;
